// ============================================================================
// bootstrapSeedResolvers — wires GridFieldSpec.seed_from keys to fetchers
// ============================================================================
// Called once at app boot. Each seed resolver receives an opaque context
// (typically `{ caseId }`) and returns row values to append to the grid.
//
// Adding a new seed source:
//   1. Add a `registerSeedResolver(<key>, async ctx => […])` block here.
//   2. Reference it from form_spec: `"seed_from": "<key>"`.

import { litigationCaseService } from 'api/services/litigationCaseService';
import { registerSeedResolver } from './seedResolver';

const PARTY_ROLE_LABEL: Record<string, string> = {
  PRIMARY:     'Applicant',
  CO_BORROWER: 'Co-Applicant',
  GUARANTOR:   'Guarantor',
};

const composeAddress = (...parts: Array<string | null | undefined>): string =>
  parts.filter((p): p is string => !!p && p.trim().length > 0).map((s) => s.trim()).join(', ');

export function bootstrapSeedResolvers(): void {
  // case_parties — pulls PRIMARY / CO_BORROWER / GUARANTOR rows for the case
  // and shapes them to the recipient-grid row schema:
  //   { party_id, party_role, recipient_name, recipient_address,
  //     recipient_mobile, recipient_email }
  // Form_spec row_fields should align with these keys for clean prefill.
  registerSeedResolver('case_parties', async (ctx) => {
    const caseId = ctx['caseId'];
    if (typeof caseId !== 'string' || !caseId) return [];
    const parties = await litigationCaseService.getParties(caseId);
    return parties.map((p) => ({
      party_id:          p.partyId,
      party_role:        p.partyRole,
      party_role_label:  PARTY_ROLE_LABEL[p.partyRole] ?? p.partyRole,
      recipient_name:    p.customerName,
      recipient_mobile:  p.mobilePrimary ?? '',
      recipient_email:   p.email ?? '',
      recipient_address: composeAddress(
        p.addressLine1, p.addressLine2, p.city, p.stateName, p.pincode,
      ),
    }));
  });
}
